import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  RefreshCw,
  Building2,
  Briefcase,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Inbox,
  CalendarDays,
  Filter,
  Users,
  Mail,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import {
  CompanyInvitationFilters,
  getCompanyInvitations,
} from '../../InvitatationSystem/Services/InvitationService';
import {
  CompanyInvitationResponse,
  CompanyInvitationStats,
} from '../../InvitatationSystem/Types/InvitationSystemType';
import { getAllRecruitersByCompanyId } from '../Services/RecruiterService';
import { RecruiterResponse } from '../Types/RecruiterTypes';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';

const PAGE_SIZE = 8;

const STATUS_OPTIONS = ['ALL', 'PENDING', 'ACCEPTED', 'REJECTED'];

const initialFilters: CompanyInvitationFilters = {
  recruiterId: '',
  fromDate: '',
  toDate: '',
  status: 'ALL',
};

const emptyStats: CompanyInvitationStats = { total: 0, pending: 0, accepted: 0, rejected: 0 };

const statusBadge = (status?: string) => {
  switch ((status || '').toUpperCase()) {
    case 'ACCEPTED':
      return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    case 'REJECTED':
      return 'bg-rose-500/10 text-rose-400 border-rose-500/30';
    case 'EXPIRED':
      return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    default:
      return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}

const StatCard = ({ label, value, icon, accent }: StatCardProps) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>
        {icon}
      </span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const RecruiterInvitationListingPage = () => {
  const [invitations, setInvitations] = useState<CompanyInvitationResponse[]>([]);
  const [pagination, setPagination] =
    useState<PaginationResponse<CompanyInvitationResponse> | null>(null);
  const [stats, setStats] = useState<CompanyInvitationStats>(emptyStats);
  const [recruiters, setRecruiters] = useState<RecruiterResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  const [recruiterId, setRecruiterId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('ALL');

  const userId = storage.getUserId() ?? '';

  const activeFilters: CompanyInvitationFilters = useMemo(
    () => ({
      recruiterId: recruiterId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      status: status !== 'ALL' ? status : undefined,
    }),
    [recruiterId, fromDate, toDate, status]
  );

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecruiters = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await getAllRecruitersByCompanyId(userId, 0, 100);
      if (response.success && response.data) {
        setRecruiters(response.data.content ?? []);
      } else {
        console.warn('Recruiter list unavailable:', response.message);
      }
    } catch (err) {
      console.error('Failed to load recruiters', err);
    }
  }, [userId]);

  const fetchInvitations = useCallback(async () => {
    if (!userId) {
      setError('No user session found. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getCompanyInvitations(
        userId,
        { ...activeFilters, search: debouncedSearch || undefined },
        page,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        const pageData = response.data.page;
        setInvitations(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setInvitations([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load invitations.');
      }
    } catch (err) {
      console.error('Failed to load invitations', err);
      setInvitations([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading invitations.');
    } finally {
      setLoading(false);
    }
  }, [userId, activeFilters, debouncedSearch, page]);

  useEffect(() => {
    fetchRecruiters();
  }, [fetchRecruiters]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Reset to the first page whenever the active filters change.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, recruiterId, fromDate, toDate, status]);

  // Recruiter options for the dropdown: API recruiters first, enriched with
  // recruiter names found on the loaded invitation rows so the list is never blank.
  const recruiterOptions = useMemo(() => {
    const map = new Map<string, { userId: string; name: string; email: string }>();

    recruiters.forEach((recruiter) => {
      if (!recruiter.userId) return;
      const name =
        `${recruiter.recruiterFirstName ?? ''} ${recruiter.recruiterLastName ?? ''}`.trim() ||
        recruiter.recruiterEmail ||
        'Recruiter';
      map.set(recruiter.userId, { userId: recruiter.userId, name, email: recruiter.recruiterEmail ?? '' });
    });

    invitations.forEach((invitation) => {
      if (!invitation.recruiterId) return;
      const existing = map.get(invitation.recruiterId);
      if (existing) {
        if ((!existing.name || existing.name === 'Recruiter') && invitation.recruiterName) {
          existing.name = invitation.recruiterName;
        }
        if (!existing.email && invitation.recruiterEmail) {
          existing.email = invitation.recruiterEmail;
        }
        return;
      }
      map.set(invitation.recruiterId, {
        userId: invitation.recruiterId,
        name: invitation.recruiterName || invitation.recruiterEmail || 'Recruiter',
        email: invitation.recruiterEmail ?? '',
      });
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [recruiters, invitations]);

  // Company-wise grouping of the visible rows.
  const groupedInvitations = useMemo(() => {
    const groups = new Map<string, CompanyInvitationResponse[]>();
    invitations.forEach((item) => {
      const key = item.companyName || 'Unknown Company';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(item);
    });
    return Array.from(groups.entries()).map(([companyName, rows]) => ({
      companyName,
      companyId: rows[0]?.companyId ?? null,
      companyLogoUrl: rows[0]?.companyLogoUrl ?? null,
      rows,
    }));
  }, [invitations]);

  const resetFilters = () => {
    setRecruiterId(initialFilters.recruiterId ?? '');
    setFromDate(initialFilters.fromDate ?? '');
    setToDate(initialFilters.toDate ?? '');
    setStatus(initialFilters.status ?? 'ALL');
    setSearch('');
  };

  const hasActiveFilters =
    Boolean(recruiterId) || Boolean(fromDate) || Boolean(toDate) || status !== 'ALL' || Boolean(search);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Inbox size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              Invitation Listing
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Company-wise invitations sent by your recruiters
            </p>
          </div>
        </div>

        <button
          onClick={fetchInvitations}
          disabled={loading}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Invitations"
          value={stats.total}
          icon={<Send size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={<Clock size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Accepted"
          value={stats.accepted}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Rejected"
          value={stats.rejected}
          icon={<XCircle size={16} />}
          accent="border-rose-500/30 bg-rose-500/10 text-rose-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          {/* Search */}
          <div className="relative xl:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search candidate, job, recruiter, status..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          {/* Recruiter */}
          <div className="relative">
            <Users size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={recruiterId}
              onChange={(e) => setRecruiterId(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              <option value="" className="bg-slate-900 text-slate-100">
                All Recruiters
              </option>
              {recruiterOptions.map((recruiter) => (
                <option
                  key={recruiter.userId}
                  value={recruiter.userId}
                  className="bg-slate-900 text-slate-100"
                >
                  {recruiter.name}
                </option>
              ))}
            </select>
          </div>

          {/* From date */}
          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition focus:border-cyan-500/50 [color-scheme:dark]"
            />
          </div>

          {/* To date */}
          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition focus:border-cyan-500/50 [color-scheme:dark]"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setStatus(option)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                status === option
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option === 'ALL' ? 'All Status' : option}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div
              key={idx}
              className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5"
            >
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : groupedInvitations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">⚡</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            No invitations found
          </p>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="mt-3 text-xs font-bold text-cyan-400 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {groupedInvitations.map((group) => (
            <div
              key={`${group.companyId ?? group.companyName}-${page}`}
              className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40"
            >
              {/* Company header */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-slate-800 bg-slate-950 p-1.5">
                    {group.companyLogoUrl ? (
                      <img
                        src={group.companyLogoUrl}
                        alt={group.companyName}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Building2 size={18} className="text-slate-500" />
                    )}
                  </span>
                  <div>
                    <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">
                      {group.companyName}
                    </h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {group.rows.length} invitation{group.rows.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-cyan-400">
                  Company
                </span>
              </div>

              {/* Invitation table */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <th className="px-5 py-3">Candidate</th>
                      <th className="px-5 py-3">Job</th>
                      <th className="px-5 py-3">Recruiter</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Invited</th>
                      <th className="px-5 py-3">Responded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((item) => (
                      <tr
                        key={item.invitationId}
                        className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-800 bg-slate-950 text-[11px] font-black text-cyan-400">
                              {(item.candidateName || 'C').charAt(0).toUpperCase()}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-slate-100">
                                {item.candidateName || 'Unknown Candidate'}
                              </p>
                              {item.candidateEmail && (
                                <p className="flex items-center gap-1 truncate text-[11px] text-slate-500">
                                  <Mail size={11} /> {item.candidateEmail}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="min-w-0">
                            <p className="flex items-center gap-1.5 truncate text-sm font-bold text-slate-200">
                              <Briefcase size={13} className="flex-shrink-0 text-cyan-400" />
                              {item.jobTitle || 'Unknown Role'}
                            </p>
                            {item.jobSummarySlug && (
                              <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                {item.jobSummarySlug}
                              </p>
                            )}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-slate-200">
                            {item.recruiterName || 'Unknown Recruiter'}
                          </p>
                          {item.recruiterEmail && (
                            <p className="truncate text-[11px] text-slate-500">{item.recruiterEmail}</p>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${statusBadge(
                              item.invitationStatus
                            )}`}
                          >
                            {item.invitationStatus || 'PENDING'}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <CalendarDays size={12} className="text-slate-500" />
                            {formatDateTime(item.invitedAt)}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <Clock size={12} className="text-slate-500" />
                            {formatDateTime(item.respondedAt)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default RecruiterInvitationListingPage;
